/**
 * Admin Agreement Detail Page
 * Shows the current body (raw markdown), metadata, all version history,
 * and lets the admin publish a new version.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { FormButton } from '../../components/form/FormButton';
import {
  useAgreement,
  useAgreementVersions,
  useUpdateAgreementMeta,
} from '../../hooks/useAgreementApi';
import { AgreementFormModal } from '../../components/admin/AgreementFormModal';

export function AgreementDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: agreement, isLoading, error } = useAgreement(slug);
  const { data: versions, isLoading: versionsLoading } =
    useAgreementVersions(slug);
  const updateMeta = useUpdateAgreementMeta();

  const [showPublishModal, setShowPublishModal] = useState(false);
  const [renameDraft, setRenameDraft] = useState<string | null>(null);

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <LoadingSpinner className="w-4 h-4" />
          <span>Loading…</span>
        </div>
      </AdminLayout>
    );
  }

  if (error || !agreement) {
    return (
      <AdminLayout>
        <p className="text-sm text-rose-600 dark:text-rose-400">
          Agreement not found.
        </p>
        <Link
          to="/admin/agreements"
          className="text-sky-600 dark:text-sky-400 hover:underline text-sm"
        >
          ← Back to agreements
        </Link>
      </AdminLayout>
    );
  }

  const applyRename = async () => {
    if (!renameDraft || !slug) return;
    try {
      await updateMeta.mutateAsync({ slug, input: { name: renameDraft } });
      setRenameDraft(null);
    } catch {
      // handled by hook
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <Link
            to="/admin/agreements"
            className="text-sky-600 dark:text-sky-400 hover:underline text-sm"
          >
            ← Back to agreements
          </Link>
          <div className="mt-2 flex items-center justify-between gap-3">
            <div>
              {renameDraft === null ? (
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {agreement.name}{' '}
                  <button
                    type="button"
                    className="text-xs text-sky-600 dark:text-sky-400 hover:underline"
                    onClick={() => setRenameDraft(agreement.name)}
                  >
                    (rename)
                  </button>
                </h1>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    autoFocus
                    value={renameDraft}
                    onChange={e => setRenameDraft(e.target.value)}
                    className="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 sm:text-sm"
                  />
                  <FormButton
                    type="button"
                    onClick={applyRename}
                    isLoading={updateMeta.isPending}
                  >
                    Save
                  </FormButton>
                  <FormButton
                    type="button"
                    variant="outline"
                    onClick={() => setRenameDraft(null)}
                  >
                    Cancel
                  </FormButton>
                </div>
              )}
              <p className="text-sm text-gray-500 dark:text-gray-400 font-mono mt-1">
                {agreement.slug}
              </p>
            </div>
            <FormButton type="button" onClick={() => setShowPublishModal(true)}>
              Publish new version
            </FormButton>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Current version
            </p>
            <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              v{agreement.current_version}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Published{' '}
              {new Date(
                agreement.current_version_published_at
              ).toLocaleString()}
            </p>
          </div>
          <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
            <p className="text-lg font-semibold">
              <span
                className={
                  agreement.enabled
                    ? 'text-emerald-700 dark:text-emerald-300'
                    : 'text-gray-500 dark:text-gray-400'
                }
              >
                {agreement.enabled ? 'Active' : 'Archived'}
              </span>
            </p>
            <button
              type="button"
              disabled={updateMeta.isPending}
              onClick={() =>
                slug &&
                updateMeta.mutate({
                  slug,
                  input: { enabled: !agreement.enabled },
                })
              }
              className="text-sm text-sky-600 dark:text-sky-400 hover:underline mt-1"
            >
              {agreement.enabled ? 'Archive' : 'Unarchive'}
            </button>
          </div>
        </div>

        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Current body (v{agreement.current_version})
          </h2>
          <pre className="whitespace-pre-wrap rounded-md border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900 text-xs sm:text-sm max-h-[30rem] overflow-auto">
            {agreement.body}
          </pre>
        </div>

        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Version history
          </h2>
          {versionsLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <LoadingSpinner className="w-4 h-4" />
              <span>Loading versions…</span>
            </div>
          )}
          {versions && versions.versions.length > 0 && (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-md">
              {versions.versions.map(v => (
                <li
                  key={v.version}
                  className="flex items-center justify-between p-3"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      v{v.version}
                      {v.version === agreement.current_version && (
                        <span className="ml-2 text-xs text-emerald-600 dark:text-emerald-400">
                          current
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Published {new Date(v.created_at).toLocaleString()} by{' '}
                      {v.created_by}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <AgreementFormModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        mode="publish-version"
        agreement={agreement}
        currentBody={agreement.body}
      />
    </AdminLayout>
  );
}
